import { useState, useEffect, useRef, Suspense } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import RouteFallback from './RouteFallback';
import { 
  Wine, 
  List, 
  X, 
  MapPin, 
  Tag, 
  BookOpen, 
  Info, 
  Phone,
  House,
  AppleLogo,
  GooglePlayLogo,
  InstagramLogo,
  FacebookLogo,
  TwitterLogo,
  Headphones,
  UserCircle,
  SignIn,
  SignOut,
  CaretDown,
  Sparkle
} from '@phosphor-icons/react';

const navLinks = [
  { name: 'Home', path: '/', icon: House },
  { name: 'Concierge Trip Planner', path: '/concierge', icon: Sparkle },
  // { name: 'Wineries', path: '/wineries', icon: Wine },  // Hidden for now
  // { name: 'Deals', path: '/deals', icon: Tag },  // Hidden for now
  { name: 'Guides', path: '/wine-country-guides', icon: MapPin },
  { name: 'Audio Tours', path: '/audio-tours', icon: Headphones },
  // { name: 'Blog', path: '/blog', icon: BookOpen },  // Hidden — content migrated into Guides
  // { name: 'Napa Valley', path: '/napa-valley', icon: MapPin },  // Hidden for now
  // { name: 'About', path: '/about', icon: Info },  // Hidden
  { name: 'Contact', path: '/contact', icon: Phone },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoginDropdownOpen, setIsLoginDropdownOpen] = useState(false);
  const [customerUser, setCustomerUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsLoginDropdownOpen(false);
  }, [location]);

  // Check for logged-in customer
  useEffect(() => {
    const userData = localStorage.getItem('customer_user');
    if (userData) {
      try {
        setCustomerUser(JSON.parse(userData));
      } catch (e) {
        setCustomerUser(null);
      }
    }
  }, [location]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsLoginDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCustomerLogout = () => {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_user');
    setCustomerUser(null);
    setIsLoginDropdownOpen(false);
  };

  const isHome = location.pathname === '/';

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || !isHome
            ? 'glass py-3'
            : 'bg-transparent py-6'
        }`}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-2 group"
            data-testid="logo-link"
          >
            <Wine 
              size={32} 
              weight="fill" 
              className={`transition-colors ${
                isScrolled || !isHome ? 'text-primary' : 'text-white'
              }`}
            />
            <span className={`font-serif text-xl font-medium transition-colors ${
              isScrolled || !isHome ? 'text-primary' : 'text-white'
            }`}>
              Winery Finder
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-primary relative group ${
                  isScrolled || !isHome ? 'text-foreground/70' : 'text-white/80'
                } ${location.pathname === link.path ? (isScrolled || !isHome ? 'text-primary' : 'text-white') : ''}`}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
              >
                {link.name}
                <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all group-hover:w-full ${
                  location.pathname === link.path ? 'w-full' : ''
                }`} />
              </Link>
            ))}
          </div>

          {/* CTA Button & Login */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Sign In / User Menu */}
            <div className="relative" ref={dropdownRef}>
              {customerUser ? (
                <>
                  <button
                    onClick={() => setIsLoginDropdownOpen(!isLoginDropdownOpen)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                      isScrolled || !isHome 
                        ? 'text-foreground/70 hover:text-primary hover:bg-muted' 
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid="user-menu-btn"
                  >
                    <UserCircle size={20} weight="fill" />
                    <span className="text-sm font-medium">{customerUser.name?.split(' ')[0] || 'Account'}</span>
                    <CaretDown size={14} className={`transition-transform ${isLoginDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {isLoginDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50"
                        data-testid="user-dropdown-menu"
                      >
                        <div className="px-4 py-2 border-b border-gray-100">
                          <p className="text-sm font-medium text-gray-900">{customerUser.name}</p>
                          <p className="text-xs text-gray-500">{customerUser.email}</p>
                        </div>
                        <Link
                          to="/my-audio-tours"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          data-testid="my-tours-link"
                        >
                          <Headphones size={18} />
                          My Audio Tours
                        </Link>
                        <button
                          onClick={handleCustomerLogout}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors w-full text-left"
                          data-testid="logout-btn"
                        >
                          <SignOut size={18} />
                          Sign Out
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              ) : (
                <Link
                  to="/signin"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    isScrolled || !isHome 
                      ? 'text-foreground/70 hover:text-primary hover:bg-muted' 
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                  data-testid="signin-link"
                >
                  <SignIn size={18} />
                  <span className="text-sm font-medium">Sign In</span>
                </Link>
              )}
            </div>

            <a
              href="https://apps.apple.com/us/app/napa-valley-winery-finder-real/id419570092"
              rel="noopener noreferrer"
              
              className="btn-gold text-sm"
              data-testid="download-app-btn"
            >
              Download App
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              isScrolled || !isHome ? 'text-foreground' : 'text-white'
            }`}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed inset-x-0 top-16 z-40 glass mx-4 rounded-lg p-6 lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-colors hover:bg-muted ${
                    location.pathname === link.path ? 'bg-muted text-primary' : 'text-foreground/70'
                  }`}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                >
                  <link.icon size={20} />
                  {link.name}
                </Link>
              ))}
              
              <hr className="border-border/50" />
              
              {/* Account Section */}
              {customerUser ? (
                <>
                  <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <UserCircle size={24} weight="fill" className="text-primary" />
                    <div>
                      <p className="text-sm font-medium">{customerUser.name}</p>
                      <p className="text-xs text-muted-foreground">{customerUser.email}</p>
                    </div>
                  </div>
                  <Link
                    to="/my-audio-tours"
                    className="flex items-center gap-3 p-3 rounded-lg transition-colors hover:bg-muted text-foreground/70"
                    data-testid="mobile-my-tours-link"
                  >
                    <Headphones size={20} />
                    My Audio Tours
                  </Link>
                  <button
                    onClick={handleCustomerLogout}
                    className="flex items-center gap-3 p-3 rounded-lg transition-colors hover:bg-red-50 text-red-600 w-full text-left"
                    data-testid="mobile-logout-btn"
                  >
                    <SignOut size={20} />
                    Sign Out
                  </button>
                </>
              ) : (
                <Link
                  to="/signin"
                  className="flex items-center gap-3 p-3 rounded-lg transition-colors hover:bg-muted text-foreground/70"
                  data-testid="mobile-signin-link"
                >
                  <SignIn size={20} />
                  Sign In
                </Link>
              )}
              
              <hr className="border-border/50" />
              
              <a
                href="https://apps.apple.com/us/app/napa-valley-winery-finder-real/id419570092"
                rel="noopener noreferrer"
                
                className="btn-gold w-full text-center"
              >
                Download App
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const Footer = () => {
  return (
    <footer className="bg-wine-900 text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <Wine size={24} weight="fill" className="text-gold-400 sm:w-7 sm:h-7" />
              <span className="font-serif text-lg sm:text-xl font-medium">Winery Finder</span>
            </div>
            <p className="text-white/60 text-xs sm:text-sm leading-relaxed mb-4 sm:mb-6">
              Discover great wineries, exclusive deals & plan your perfect Napa Valley and Sonoma trip.
            </p>
            <div className="flex gap-3 sm:gap-4">
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-1.5 sm:p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                data-testid="social-facebook"
              >
                <FacebookLogo size={16} className="sm:w-5 sm:h-5" />
              </a>
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-1.5 sm:p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                data-testid="social-instagram"
              >
                <InstagramLogo size={16} className="sm:w-5 sm:h-5" />
              </a>
              <a 
                href="https://twitter.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-1.5 sm:p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                data-testid="social-twitter"
              >
                <TwitterLogo size={16} className="sm:w-5 sm:h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-base sm:text-lg mb-3 sm:mb-4">Explore</h4>
            <ul className="space-y-1.5 sm:space-y-2">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path}
                    className="text-white/60 hover:text-white transition-colors text-xs sm:text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Download App */}
          <div className="col-span-2 md:col-span-1">
            <h4 className="font-serif text-base sm:text-lg mb-3 sm:mb-4">Get the App</h4>
            <p className="text-white/60 text-xs sm:text-sm mb-3 sm:mb-4">
              Download our FREE app to find wine tasting deals near you!
            </p>
            <div className="flex flex-row md:flex-col gap-2 sm:gap-3">
              <a
                href="https://apps.apple.com/us/app/napa-valley-winery-finder-real/id419570092"
                rel="noopener noreferrer"
                
                className="flex-1 md:flex-none flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors rounded-lg px-3 sm:px-4 py-2 sm:py-3"
                data-testid="app-store-link"
              >
                <AppleLogo size={20} weight="fill" className="sm:w-6 sm:h-6" />
                <div className="text-left">
                  <div className="text-[8px] sm:text-xs text-white/60">Download on the</div>
                  <div className="text-xs sm:text-sm font-medium">App Store</div>
                </div>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.shoutem.n75202&hl=en"
                rel="noopener noreferrer"
                
                className="flex-1 md:flex-none flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors rounded-lg px-3 sm:px-4 py-2 sm:py-3"
                data-testid="play-store-link"
              >
                <GooglePlayLogo size={20} weight="fill" className="sm:w-6 sm:h-6" />
                <div className="text-left">
                  <div className="text-[8px] sm:text-xs text-white/60">Get it on</div>
                  <div className="text-xs sm:text-sm font-medium">Google Play</div>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-3 sm:gap-4">
          <p className="text-white/40 text-xs sm:text-sm text-center md:text-left">
            &copy; {new Date().getFullYear()} Winery Finder App. All rights reserved.
          </p>
          <div className="flex gap-4 sm:gap-6 text-xs sm:text-sm text-white/40">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            {/* AI Disclosure link temporarily hidden per user request (Feb 2026).
                Page remains routable for compliance crawlers via direct URL. */}
          </div>
        </div>
        {/* Site-wide AI disclosure footnote: temporarily hidden per user
            request (Feb 2026). Compliance posture is preserved via the
            <meta name="ai-generated-content"> tag in public/index.html
            and the still-routable /ai-disclosure page. Re-mount the <p>
            below when ready to re-surface the disclosure publicly. */}
      </div>
    </footer>
  );
};

const Layout = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        {/* Lazy pages suspend here, so the navbar/footer stay put */}
        <Suspense fallback={<RouteFallback />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
